import filter from 'lodash/filter'
import flatMap from 'lodash/flatMap'
import sumBy from 'lodash/sumBy'
import map from 'lodash/map'

import {
  IProject,
} from '@SiteModules/Project/types'

export interface IBulkExportationInfo {
  readonly estimatedNumberOfExportationFiles: number
  readonly projectVersionIdsToExportWithoutSubtitles: string[]
  readonly subtitleIdsToExportAsSubtitles: number[]
  readonly subtitleIdsToExportAsAudiosAndVideos: number[],
}

export default function getEstimatedNumberOfExportationFiles(
  editingProjects: IProject[],
  willExportOriginalVideos: boolean,
  willExportAllClips: boolean,
  selectedLanguages: string[],
  selectedFormats: string[],
  shouldHaveOneAudioFilePerSpeaker: boolean
): IBulkExportationInfo {
  const relevantProjectVersions = flatMap(editingProjects, project => filter(project.projectVersions, projectVersion => (willExportOriginalVideos && !projectVersion.attributes.isRepurposed) || (willExportAllClips && projectVersion.attributes.isRepurposed)))
  const relevantProjectVersionIds = map(relevantProjectVersions, projectVersion => projectVersion.id)
  const numberOfRelevantProjectVersions = relevantProjectVersions.length

  if (numberOfRelevantProjectVersions === 0) {
    return { estimatedNumberOfExportationFiles: 0, projectVersionIdsToExportWithoutSubtitles: [], subtitleIdsToExportAsSubtitles: [], subtitleIdsToExportAsAudiosAndVideos: [] }
  }

  const numberOfSelectedSubtitlesFormats = filter(selectedFormats, selectedFormat => selectedFormat.includes('Subtitles')).length
  const numberOfSelectedVideoFormats = filter(selectedFormats, selectedFormat => selectedFormat.includes('Video')).length
  const numberOfSelectedAudioFormats = selectedFormats.length - numberOfSelectedSubtitlesFormats - numberOfSelectedVideoFormats

  let estimatedNumberOfExportationFiles = (numberOfSelectedAudioFormats + numberOfSelectedVideoFormats) * numberOfRelevantProjectVersions

  if (selectedLanguages.length === 0) {
    return { estimatedNumberOfExportationFiles, projectVersionIdsToExportWithoutSubtitles: relevantProjectVersionIds, subtitleIdsToExportAsSubtitles: [], subtitleIdsToExportAsAudiosAndVideos: [] }
  }

  const relevantProjectVersionsWithoutSubtitles = filter(relevantProjectVersions, relevantProjectVersion => relevantProjectVersion.subtitles.length === 0)
  const relevantProjectVersionsWithoutSubtitleIds = map(relevantProjectVersionsWithoutSubtitles, projectVersion => projectVersion.id)
  const numberOfRelevantProjectVersionsWithoutSubtitles = relevantProjectVersionsWithoutSubtitles.length
  const numberOfVideoOrAudioFilesForProjectVersionsWithoutSubtitles = (numberOfSelectedVideoFormats + numberOfSelectedAudioFormats) * numberOfRelevantProjectVersionsWithoutSubtitles

  const relevantSubtitlesInSelectedLanguages = flatMap(relevantProjectVersions, projectVersion => filter(projectVersion.subtitles, subtitle => selectedLanguages.includes(subtitle.attributes.language)))
  const relevantSubtitlesInSelectedLanguagesThatCanBeExportedAsAudiosAndVideos = filter(relevantSubtitlesInSelectedLanguages, subtitle => subtitle.attributes.canBeExported)
  const relevantSubtitleIdsInSelectedLanguagesThatCanBeExported = map(relevantSubtitlesInSelectedLanguagesThatCanBeExportedAsAudiosAndVideos, subtitle => subtitle.id)
  const numberOfRelevantSubtitlesInSelectedLanguagesThatCanBeExported = relevantSubtitlesInSelectedLanguagesThatCanBeExportedAsAudiosAndVideos.length
  const numberOfVideoFilesForSubtitles = numberOfSelectedVideoFormats * numberOfRelevantSubtitlesInSelectedLanguagesThatCanBeExported
  const numberOfAudioFilesForSubtitles = numberOfSelectedAudioFormats * numberOfRelevantSubtitlesInSelectedLanguagesThatCanBeExported
  const relevantSubtitlesInSelectedLanguagesThatCanBeExportedAsSubtitles = filter(relevantSubtitlesInSelectedLanguages, subtitle => subtitle.attributes.canBeExportedAsSubtitles)
  const relevantSubtitleIdsInSelectedLanguagesThatCanBeExportedAsSubtitles = map(relevantSubtitlesInSelectedLanguagesThatCanBeExportedAsSubtitles, subtitle => subtitle.id)
  const numberOfSubtitleFiles = numberOfSelectedSubtitlesFormats * relevantSubtitlesInSelectedLanguagesThatCanBeExportedAsSubtitles.length

  estimatedNumberOfExportationFiles = numberOfVideoOrAudioFilesForProjectVersionsWithoutSubtitles + numberOfVideoFilesForSubtitles + numberOfAudioFilesForSubtitles + numberOfSubtitleFiles

  if (!shouldHaveOneAudioFilePerSpeaker) {
    return { estimatedNumberOfExportationFiles, projectVersionIdsToExportWithoutSubtitles: relevantProjectVersionsWithoutSubtitleIds, subtitleIdsToExportAsSubtitles: relevantSubtitleIdsInSelectedLanguagesThatCanBeExportedAsSubtitles, subtitleIdsToExportAsAudiosAndVideos: relevantSubtitleIdsInSelectedLanguagesThatCanBeExported }
  }

  const subtitlesWithMoreThanOneVoiceSpeaker = filter(relevantSubtitlesInSelectedLanguagesThatCanBeExportedAsAudiosAndVideos, subtitle => subtitle.attributes.numberOfVoiceSpeakers > 1)

  const numberOfAdditionalVoiceSpeakersInTheseSubtitles = sumBy(subtitlesWithMoreThanOneVoiceSpeaker, subtitle => subtitle.attributes.numberOfVoiceSpeakers - 1)
  const numberOfAdditionalAudioFilesForSpeakers = numberOfAdditionalVoiceSpeakersInTheseSubtitles * numberOfSelectedAudioFormats

  estimatedNumberOfExportationFiles = estimatedNumberOfExportationFiles + numberOfAdditionalAudioFilesForSpeakers

  return { estimatedNumberOfExportationFiles, projectVersionIdsToExportWithoutSubtitles: relevantProjectVersionsWithoutSubtitleIds, subtitleIdsToExportAsSubtitles: relevantSubtitleIdsInSelectedLanguagesThatCanBeExportedAsSubtitles, subtitleIdsToExportAsAudiosAndVideos: relevantSubtitleIdsInSelectedLanguagesThatCanBeExported }
}
